import React, { useState } from 'react';
import {
  Form, Button, Card, Row, Col, Badge, ButtonGroup, Spinner,
} from 'react-bootstrap';
import ServiceOfferService from '../../../Services/ServiceOfferService';

const DEFAULT_COLUMNS = ['Vehicle Type', 'Rate'];

function ServiceTableEditor({ tables, onChange }) {
  const [newColumnInputs, setNewColumnInputs] = useState({});
  const [calculatingTotal, setCalculatingTotal] = useState({});

  const addTable = () => {
    onChange([
      ...tables,
      {
        title: '',
        subtitle: '',
        columns: [...DEFAULT_COLUMNS],
        rows: [{ cells: DEFAULT_COLUMNS.map(() => '') }],
        totals: [],
      },
    ]);
  };

  const cloneTable = (tableIndex) => {
    const source = tables[tableIndex];
    const cloned = {
      ...source,
      title: `${source.title} (Copy)`,
      columns: [...source.columns],
      rows: source.rows.map((r) => ({ cells: [...r.cells] })),
      totals: (source.totals || []).map((t) => ({ ...t })),
    };
    const updated = [...tables];
    updated.splice(tableIndex + 1, 0, cloned);
    onChange(updated);
  };

  const removeTable = (tableIndex) => {
    onChange(tables.filter((_, i) => i !== tableIndex));
  };

  const updateTableField = (tableIndex, field, value) => {
    const updated = [...tables];
    updated[tableIndex] = { ...updated[tableIndex], [field]: value };
    onChange(updated);
  };

  // Column management
  const addColumn = (tableIndex) => {
    const colName = (newColumnInputs[tableIndex] || '').trim();
    if (!colName) return;
    const updated = [...tables];
    const table = { ...updated[tableIndex] };
    table.columns = [...table.columns, colName];
    table.rows = table.rows.map((row) => ({
      cells: [...row.cells, ''],
    }));
    updated[tableIndex] = table;
    onChange(updated);
    setNewColumnInputs((prev) => ({ ...prev, [tableIndex]: '' }));
  };

  const removeColumn = (tableIndex, colIndex) => {
    const updated = [...tables];
    const table = { ...updated[tableIndex] };
    if (table.columns.length <= 1) return;
    table.columns = table.columns.filter((_, i) => i !== colIndex);
    table.rows = table.rows.map((row) => ({
      cells: row.cells.filter((_, i) => i !== colIndex),
    }));
    updated[tableIndex] = table;
    onChange(updated);
  };

  // Row management
  const addRow = (tableIndex) => {
    const updated = [...tables];
    const table = { ...updated[tableIndex] };
    table.rows = [
      ...table.rows,
      { cells: table.columns.map(() => '') },
    ];
    updated[tableIndex] = table;
    onChange(updated);
  };

  const removeRow = (tableIndex, rowIndex) => {
    const updated = [...tables];
    const table = { ...updated[tableIndex] };
    table.rows = table.rows.filter((_, i) => i !== rowIndex);
    updated[tableIndex] = table;
    onChange(updated);
  };

  const updateCell = (tableIndex, rowIndex, cellIndex, value) => {
    const updated = [...tables];
    const table = { ...updated[tableIndex] };
    const rows = [...table.rows];
    const cells = [...rows[rowIndex].cells];
    cells[cellIndex] = value;
    rows[rowIndex] = { cells };
    table.rows = rows;
    updated[tableIndex] = table;
    onChange(updated);
  };

  // Totals management
  const addTotal = (tableIndex) => {
    const updated = [...tables];
    const table = { ...updated[tableIndex] };
    table.totals = [...(table.totals || []), { label: '', value: '' }];
    updated[tableIndex] = table;
    onChange(updated);
  };

  const removeTotal = (tableIndex, totalIndex) => {
    const updated = [...tables];
    const table = { ...updated[tableIndex] };
    table.totals = (table.totals || []).filter((_, i) => i !== totalIndex);
    updated[tableIndex] = table;
    onChange(updated);
  };

  const updateTotal = (tableIndex, totalIndex, field, value) => {
    const updated = [...tables];
    const table = { ...updated[tableIndex] };
    const totals = [...(table.totals || [])];
    totals[totalIndex] = { ...totals[totalIndex], [field]: value };
    table.totals = totals;
    updated[tableIndex] = table;
    onChange(updated);
  };

  const handleColumnKeyPress = (e, tableIndex) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addColumn(tableIndex);
    }
  };

  const calculateTotal = async (tableIndex) => {
    const table = tables[tableIndex];
    if (!table.rows || table.rows.length === 0) return;

    setCalculatingTotal((prev) => ({ ...prev, [tableIndex]: true }));

    try {
      const response = await ServiceOfferService.calculateTotal(table);
      const totals = response?.data?.data?.totals;
      if (totals && totals.length > 0) {
        const updated = [...tables];
        updated[tableIndex] = {
          ...updated[tableIndex],
          totals: [...(updated[tableIndex].totals || []), ...totals],
        };
        onChange(updated);
      }
    } catch {
      // Fallback: add empty total row for manual entry
      addTotal(tableIndex);
    } finally {
      setCalculatingTotal((prev) => ({ ...prev, [tableIndex]: false }));
    }
  };

  return (
    <div>
      {tables.map((table, tableIndex) => (
        // eslint-disable-next-line react/no-array-index-key
        <Card key={tableIndex} className="mb-3 border">
          <Card.Header className="d-flex justify-content-between align-items-center bg-light">
            <strong>
              Table
              {' '}
              {tableIndex + 1}
            </strong>
            <ButtonGroup size="sm">
              <Button
                variant="outline-primary"
                onClick={() => cloneTable(tableIndex)}
                title="Clone table"
              >
                <i className="fa fa-copy me-1" />
                Clone
              </Button>
              <Button
                variant="outline-danger"
                onClick={() => removeTable(tableIndex)}
              >
                <i className="fa fa-times me-1" />
                Remove
              </Button>
            </ButtonGroup>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Table Title</Form.Label>
                  <Form.Control
                    type="text"
                    value={table.title}
                    onChange={(e) => updateTableField(
                      tableIndex,
                      'title',
                      e.target.value,
                    )}
                    placeholder="e.g. May 29 & May 31 – up to 8 hours"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Subtitle (optional)
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={table.subtitle || ''}
                    onChange={(e) => updateTableField(
                      tableIndex,
                      'subtitle',
                      e.target.value,
                    )}
                    placeholder="Optional context"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Columns management */}
            <div className="mb-3">
              <Form.Label className="fw-semibold">Columns</Form.Label>
              <div className="d-flex flex-wrap gap-2 mb-2">
                {table.columns.map((col, colIndex) => (
                  <Badge
                    // eslint-disable-next-line react/no-array-index-key
                    key={`col-${colIndex}`}
                    bg="dark"
                    className="d-flex align-items-center gap-1 py-2 px-3"
                  >
                    {col}
                    {table.columns.length > 1 && (
                      <button
                        type="button"
                        className="btn-close btn-close-white btn-close-sm ms-1"
                        style={{ fontSize: '0.4rem' }}
                        onClick={() => removeColumn(tableIndex, colIndex)}
                        aria-label="Remove column"
                      />
                    )}
                  </Badge>
                ))}
              </div>
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  size="sm"
                  value={newColumnInputs[tableIndex] || ''}
                  onChange={(e) => setNewColumnInputs((prev) => ({
                    ...prev,
                    [tableIndex]: e.target.value,
                  }))}
                  onKeyPress={(e) => handleColumnKeyPress(e, tableIndex)}
                  placeholder="Add column name..."
                  style={{ maxWidth: '200px' }}
                />
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => addColumn(tableIndex)}
                  disabled={!(newColumnInputs[tableIndex] || '').trim()}
                >
                  <i className="fa fa-plus me-1" />
                  Add Column
                </Button>
              </div>
            </div>

            {/* Table rows */}
            <div className="mb-2">
              <div className="d-flex gap-2 mb-2 px-1">
                {table.columns.map((col, colIndex) => (
                  <small
                    // eslint-disable-next-line react/no-array-index-key
                    key={`label-${colIndex}`}
                    className="fw-bold text-muted"
                    style={{ flex: 1 }}
                  >
                    {col}
                  </small>
                ))}
                <div style={{ width: '38px' }} />
              </div>

              {table.rows.map((row, rowIndex) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={rowIndex} className="d-flex gap-2 mb-2 align-items-center">
                  {table.columns.map((col, cellIndex) => (
                    <Form.Control
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${rowIndex}-${cellIndex}`}
                      type="text"
                      value={row.cells[cellIndex] || ''}
                      onChange={(e) => updateCell(
                        tableIndex,
                        rowIndex,
                        cellIndex,
                        e.target.value,
                      )}
                      placeholder={col}
                      style={{ flex: 1 }}
                    />
                  ))}
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeRow(tableIndex, rowIndex)}
                    disabled={table.rows.length <= 1}
                    title="Remove row"
                  >
                    <i className="fa fa-minus" />
                  </Button>
                </div>
              ))}
            </div>

            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => addRow(tableIndex)}
              className="me-2"
            >
              <i className="fa fa-plus me-1" />
              Add Row
            </Button>

            {/* Totals section */}
            <div className="mt-3 pt-3 border-top">
              <Form.Label className="fw-semibold">
                Totals (optional)
              </Form.Label>
              {(table.totals || []).map((total, totalIndex) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`total-${totalIndex}`} className="d-flex gap-2 mb-2 align-items-center">
                  <Form.Control
                    type="text"
                    value={total.label}
                    onChange={(e) => updateTotal(
                      tableIndex,
                      totalIndex,
                      'label',
                      e.target.value,
                    )}
                    placeholder="e.g. TOTAL PRICE"
                    style={{ flex: 2 }}
                  />
                  <Form.Control
                    type="text"
                    value={total.value}
                    onChange={(e) => updateTotal(
                      tableIndex,
                      totalIndex,
                      'value',
                      e.target.value,
                    )}
                    placeholder="e.g. 48,750 EUR"
                    style={{ flex: 1 }}
                  />
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeTotal(tableIndex, totalIndex)}
                    title="Remove total"
                  >
                    <i className="fa fa-minus" />
                  </Button>
                </div>
              ))}
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => addTotal(tableIndex)}
                >
                  <i className="fa fa-plus me-1" />
                  Add Total Row
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => calculateTotal(tableIndex)}
                  disabled={
                    calculatingTotal[tableIndex]
                    || !tables[tableIndex].rows
                    || tables[tableIndex].rows.length === 0
                  }
                >
                  {calculatingTotal[tableIndex] ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-1"
                      />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-calculator me-1" />
                      Calculate Total
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      ))}

      <Button
        variant="outline-primary"
        onClick={addTable}
      >
        <i className="fa fa-plus me-1" />
        Add Service Table
      </Button>
    </div>
  );
}

export default ServiceTableEditor;
